import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { useQueryClient } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";
import { api, errorMessage, post, type AuthStatus } from "../api";
import { ErrorNote } from "../components/feedback";
import { Logo, passkeyError } from "../components/Shell";
import { Icon } from "../icons";

/** Anmeldung mit Passkey; Ersteinrichtung des Owners nur mit Einrichtungscode (docs/09 §3). */
export function Login({ status }: { status: AuthStatus }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const nameId = useId();
  const codeId = useId();
  const setup = status.setupRequired;

  const done = async () => {
    await queryClient.invalidateQueries();
  };

  const login = async () => {
    setBusy(true);
    setError(null);
    try {
      const { challengeId, options } = await post<{
        challengeId: string;
        options: Parameters<typeof startAuthentication>[0]["optionsJSON"];
      }>("/api/v1/auth/login/options");
      const response = await startAuthentication({ optionsJSON: options });
      await post("/api/v1/auth/login/verify", { challengeId, response });
      await done();
    } catch (err) {
      setError(passkeyError(err));
    } finally {
      setBusy(false);
    }
  };

  const register = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !code.trim()) {
      setError("Bitte Namen und Einrichtungscode angeben.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { challengeId, options } = await post<{
        challengeId: string;
        options: Parameters<typeof startRegistration>[0]["optionsJSON"];
      }>("/api/v1/auth/setup/options", { setupCode: code, displayName: name });
      const response = await startRegistration({ optionsJSON: options });
      await post("/api/v1/auth/setup/verify", { challengeId, setupCode: code, displayName: name, response });
      await done();
    } catch (err) {
      setError(passkeyError(err));
    } finally {
      setBusy(false);
    }
  };

  const devLogin = async () => {
    try {
      await post("/api/v1/auth/dev-login");
      await done();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <main className="login" id="main">
      <div className="panel login-card">
        <Logo />
        <h1>{setup ? "szyOS einrichten" : "Anmelden"}</h1>
        {setup ? (
          status.setupEnabled ? (
            <form onSubmit={register} className="form" noValidate>
              <p className="muted">
                Lege den Owner an. Der Passkey bleibt auf deinem Gerät, szyOS speichert nur den öffentlichen
                Schlüssel.
              </p>
              <label htmlFor={nameId}>Dein Name</label>
              <input
                id={nameId}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
              />
              <label htmlFor={codeId}>Einrichtungscode</label>
              <input
                id={codeId}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="one-time-code"
                required
              />
              <ErrorNote error={error} />
              <button type="submit" className="btn btn--primary" disabled={busy}>
                <span className="btn-label">
                  <Icon name="key" /> Passkey erstellen
                </span>
              </button>
            </form>
          ) : (
            <p className="form-error" role="alert">
              Ersteinrichtung ist deaktiviert. Starte den Server mit SZYOS_SETUP_CODE.
            </p>
          )
        ) : (
          <div className="form">
            <p className="muted">
              Melde dich mit deinem Passkey an (Windows Hello, Touch ID, Handy oder Sicherheitsschlüssel).
            </p>
            <ErrorNote error={error} />
            <button type="button" className="btn btn--primary" onClick={() => void login()} disabled={busy}>
              <span className="btn-label">
                <Icon name="key" /> Mit Passkey anmelden
              </span>
            </button>
          </div>
        )}
        {import.meta.env.DEV && (
          <button type="button" className="btn btn--ghost dev-login" onClick={() => void devLogin()}>
            Entwicklungs-Login (nur mit SZYOS_DEV_LOGIN=1)
          </button>
        )}
      </div>
    </main>
  );
}

export const authQuery = { queryKey: ["auth"], queryFn: api.authStatus };
